/**
 * YahtzeeBoard - Game board with dice, scorecard, and controls
 *
 * Features:
 * - 5 animated dice with dot/pip faces (click to hold)
 * - Roll button with rolls remaining indicator
 * - Full scorecard with all 13 categories + bonus
 * - Score preview on hover
 * - Current player indicator
 */

import React, { useState } from 'react';
import {
  YahtzeeGameState,
  YahtzeePlayer,
  ScoreCategory,
  Die,
  DieValue,
  UPPER_CATEGORIES,
  LOWER_CATEGORIES,
  ALL_CATEGORIES,
  CATEGORY_LABELS,
  MAX_ROLLS,
} from '../types';
import { YahtzeeEngine } from '../YahtzeeEngine';
import './YahtzeeBoard.css';

interface YahtzeeBoardProps {
  gameState: YahtzeeGameState;
  engine: YahtzeeEngine;
  isRolling: boolean;
  isHumanTurn: boolean;
  onRoll: () => void;
  onToggleHold: (dieIndex: number) => void;
  onScoreCategory: (category: ScoreCategory) => void;
  isGameOver: boolean;
}

// ============================================================================
// Die Face Component - renders pips (dots) based on value
// ============================================================================

const DieFace: React.FC<{ value: DieValue }> = ({ value }) => {
  const renderPips = () => {
    switch (value) {
      case 1:
        return <span className="yahtzee-die__pip yahtzee-die__pip--center" />;
      case 2:
        return (
          <>
            <span className="yahtzee-die__pip yahtzee-die__pip--top-right" />
            <span className="yahtzee-die__pip yahtzee-die__pip--bottom-left" />
          </>
        );
      case 3:
        return (
          <>
            <span className="yahtzee-die__pip yahtzee-die__pip--top-right" />
            <span className="yahtzee-die__pip yahtzee-die__pip--center" />
            <span className="yahtzee-die__pip yahtzee-die__pip--bottom-left" />
          </>
        );
      case 4:
        return (
          <>
            <span className="yahtzee-die__pip yahtzee-die__pip--top-left" />
            <span className="yahtzee-die__pip yahtzee-die__pip--top-right" />
            <span className="yahtzee-die__pip yahtzee-die__pip--bottom-left" />
            <span className="yahtzee-die__pip yahtzee-die__pip--bottom-right" />
          </>
        );
      case 5:
        return (
          <>
            <span className="yahtzee-die__pip yahtzee-die__pip--top-left" />
            <span className="yahtzee-die__pip yahtzee-die__pip--top-right" />
            <span className="yahtzee-die__pip yahtzee-die__pip--center" />
            <span className="yahtzee-die__pip yahtzee-die__pip--bottom-left" />
            <span className="yahtzee-die__pip yahtzee-die__pip--bottom-right" />
          </>
        );
      case 6:
        return (
          <>
            <span className="yahtzee-die__pip yahtzee-die__pip--top-left" />
            <span className="yahtzee-die__pip yahtzee-die__pip--top-right" />
            <span className="yahtzee-die__pip yahtzee-die__pip--mid-left" />
            <span className="yahtzee-die__pip yahtzee-die__pip--mid-right" />
            <span className="yahtzee-die__pip yahtzee-die__pip--bottom-left" />
            <span className="yahtzee-die__pip yahtzee-die__pip--bottom-right" />
          </>
        );
      default:
        return null;
    }
  };

  return <div className="yahtzee-die__face">{renderPips()}</div>;
};

// ============================================================================
// Main Board Component
// ============================================================================

export const YahtzeeBoard: React.FC<YahtzeeBoardProps> = ({
  gameState,
  engine,
  isRolling,
  isHumanTurn,
  onRoll,
  onToggleHold,
  onScoreCategory,
  isGameOver,
}) => {
  const [hoveredCategory, setHoveredCategory] = useState<ScoreCategory | null>(null);

  const currentPlayer = gameState.players[gameState.currentPlayerIndex];
  const dice = gameState.dice;
  const rollsRemaining = gameState.rollsRemaining;
  const hasRolled = rollsRemaining < MAX_ROLLS;
  const canRoll = rollsRemaining > 0 && isHumanTurn && !isRolling && !isGameOver;
  const canScore = hasRolled && isHumanTurn && !isRolling && !isGameOver;

  // Get potential scores for preview
  const potentialScores = hasRolled ? engine.getPotentialScores() : null;

  const renderDice = () => (
    <div className="yahtzee-board__dice-area">
      <div className="yahtzee-board__dice-row">
        {dice.map((die: Die, index: number) => (
          <button
            key={index}
            className={`yahtzee-die ${die.held ? 'yahtzee-die--held' : ''} ${
              isRolling && !die.held ? 'yahtzee-die--rolling' : ''
            }`}
            onClick={() => {
              if (isHumanTurn && hasRolled && !isGameOver) {
                onToggleHold(index);
              }
            }}
            disabled={!isHumanTurn || !hasRolled || isGameOver}
            aria-label={`Die ${index + 1}: ${die.value}${die.held ? ' (held)' : ''}`}
          >
            <DieFace value={die.value} />
            {die.held && <span className="yahtzee-die__held-label">HELD</span>}
          </button>
        ))}
      </div>

      <div className="yahtzee-board__roll-controls">
        <button
          className="yahtzee-board__roll-btn"
          onClick={onRoll}
          disabled={!canRoll}
        >
          {rollsRemaining === MAX_ROLLS ? 'Roll Dice' : 'Roll Again'}
        </button>
        <span className="yahtzee-board__rolls-remaining">
          Rolls left: {rollsRemaining}
        </span>
      </div>
    </div>
  );

  const renderScorecard = (player: YahtzeePlayer, isActive: boolean) => {
    const upperTotal = engine.getUpperSectionTotal(player);
    const upperBonus = engine.getUpperBonus(player);
    const lowerTotal = engine.getLowerSectionTotal(player);
    const yahtzeeBonus = engine.getYahtzeeBonusTotal(player);
    const grandTotal = engine.getTotalScore(player);

    const renderCategoryRow = (category: ScoreCategory) => {
      const scored = player.scorecard.scores[category];
      const isScored = scored !== undefined;
      const isAvailable = !isScored && isActive && canScore;
      const previewScore =
        isAvailable && potentialScores ? potentialScores[category] : null;
      const isHovered = hoveredCategory === category && isAvailable;

      return (
        <tr
          key={category}
          className={`yahtzee-scorecard__row ${
            isScored ? 'yahtzee-scorecard__row--scored' : ''
          } ${isAvailable ? 'yahtzee-scorecard__row--available' : ''} ${
            isHovered ? 'yahtzee-scorecard__row--hovered' : ''
          }`}
          onMouseEnter={() => isAvailable && setHoveredCategory(category)}
          onMouseLeave={() => setHoveredCategory(null)}
          onClick={() => isAvailable && onScoreCategory(category)}
        >
          <td className="yahtzee-scorecard__category-name">
            {CATEGORY_LABELS[category]}
          </td>
          <td className="yahtzee-scorecard__score">
            {isScored ? (
              <span className="yahtzee-scorecard__score-value">{scored}</span>
            ) : isHovered && previewScore !== null ? (
              <span className="yahtzee-scorecard__score-preview">
                {previewScore}
              </span>
            ) : (
              <span className="yahtzee-scorecard__score-empty">-</span>
            )}
          </td>
        </tr>
      );
    };

    return (
      <div
        className={`yahtzee-scorecard ${
          isActive ? 'yahtzee-scorecard--active' : ''
        }`}
      >
        <div className="yahtzee-scorecard__player-name">
          {player.name}
          {isActive && !isGameOver && (
            <span className="yahtzee-scorecard__turn-indicator">Current Turn</span>
          )}
        </div>

        <table className="yahtzee-scorecard__table">
          <thead>
            <tr>
              <th className="yahtzee-scorecard__header">Category</th>
              <th className="yahtzee-scorecard__header">Score</th>
            </tr>
          </thead>
          <tbody>
            {/* Upper Section */}
            <tr className="yahtzee-scorecard__section-header">
              <td colSpan={2}>Upper Section</td>
            </tr>
            {UPPER_CATEGORIES.map((cat) => renderCategoryRow(cat))}
            <tr className="yahtzee-scorecard__subtotal">
              <td>Upper Total</td>
              <td>{upperTotal}/63</td>
            </tr>
            <tr className="yahtzee-scorecard__subtotal">
              <td>Bonus (63+)</td>
              <td>{upperBonus > 0 ? `+${upperBonus}` : '-'}</td>
            </tr>

            {/* Lower Section */}
            <tr className="yahtzee-scorecard__section-header">
              <td colSpan={2}>Lower Section</td>
            </tr>
            {LOWER_CATEGORIES.map((cat) => renderCategoryRow(cat))}
            <tr className="yahtzee-scorecard__subtotal">
              <td>Yahtzee Bonus</td>
              <td>{yahtzeeBonus > 0 ? `+${yahtzeeBonus}` : '-'}</td>
            </tr>

            {/* Grand Total */}
            <tr className="yahtzee-scorecard__grand-total">
              <td>Grand Total</td>
              <td>{grandTotal}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="yahtzee-board">
      {/* Turn Indicator */}
      <div className="yahtzee-board__turn-info">
        <span className="yahtzee-board__current-player">
          {currentPlayer.name}&apos;s Turn
        </span>
        <span className="yahtzee-board__round-info">
          Round {Math.min(gameState.currentRound, 13)} / 13
        </span>
      </div>

      {/* Dice Area */}
      {renderDice()}

      {/* Scorecards */}
      <div className="yahtzee-board__scorecards">
        {gameState.players.map((player, index) =>
          renderScorecard(player, index === gameState.currentPlayerIndex)
        )}
      </div>
    </div>
  );
};
